'use client';

import { useCommendaOSList } from '@commenda/api/commenda-os-dsl';
import { LoadingIndicator, MultiSelect } from '@commenda/kakashi';
import type { FormComponent, ValidationRules } from '@commenda/types';
import type { DSLContext } from '@commenda/types/src/commenda-os-dsl';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormContext } from '../../../hooks/FormContext';
import { useEntryCompleteness } from '../../../hooks/useEntryCompleteness';
import { EntryCompletenessHelper } from '../../../utils/entry-completeness-helper';
import type {
  EntryCompletenessStatus,
  EntryCompletenessTarget,
} from '../../../utils/entry-completeness.types';
import { LinkedFormDslHelper } from '../../../utils/linked-form-dsl-helper';
import { isRuleActive } from '../../../utils/validation';
import { LabelWithGuidanceTooltip } from '../LabelWithGuidanceTooltip';
import { EntityCard } from './EntityCard';
import { InlineFieldsContainer } from './InlineFieldsContainer';

// Represents a selected entity (either existing or new)
export interface SelectedEntity {
  id: string; // Entity ID or 'new-{uuid}' for new entities
  displayName: string;
  isNew: boolean;
  hasMissingFields?: boolean;
  /** For an existing entity: why its saved record cannot be accepted yet, if it cannot. */
  completenessStatus?: EntryCompletenessStatus;
}

interface EntitySelectOrCreateProps {
  fieldId: string;
  label?: string;
  description?: string;
  placeholder?: string;
  guidance?: { text?: string };
  validation?: {
    rules?: ValidationRules;
  };
  props?: {
    entity?: string;
    displayField?: string;
    valueField?: string;
    listPath?: string;
    minSelection?: number;
    maxSelection?: number;
    addNewLabel?: string;
    selectLabel?: string;
    disabled?: boolean;
    /** The context key for identifying this entity (e.g., 'corporationId'). Comes from entity schema. */
    idContextKey?: string;
  };
  // Child AutoDetect fields configured by form creator
  template?: FormComponent[];
}

/**
 * EntitySelectOrCreate Component
 *
 * A component that allows selecting multiple existing entities from a dropdown
 * with the ability to create new entities inline. Selected entities are displayed
 * as cards with edit/remove actions. When editing, AutoDetect fields are shown
 * inline below the entity card.
 *
 * Key features:
 * - Multi-select dropdown with "Add new" option
 * - Selected entities displayed as cards
 * - Inline expansion for editing entity fields
 * - Context switching per entity for AutoDetect fields
 * - Support for creating new entities
 */
export const EntitySelectOrCreateComponent = ({
  fieldId,
  label,
  description,
  placeholder,
  guidance,
  validation,
  props = {},
  template = [],
}: EntitySelectOrCreateProps) => {
  const {
    formData,
    updateField,
    errors,
    validateField,
    isFieldVisible,
    isFieldDisabled,
    dslContext,
  } = useContext(FormContext);

  const {
    entity = 'corporation',
    displayField = 'generalCorporationInfo.legalName',
    valueField = 'id',
    listPath,
    minSelection = 1,
    maxSelection = 1,
    addNewLabel,
    selectLabel,
    disabled: propsDisabled = false,
    idContextKey,
  } = props;

  // State for expanded entities (showing inline fields)
  const [expandedEntityIds, setExpandedEntityIds] = useState<Set<string>>(
    new Set()
  );

  // State for new entity counter (for generating unique IDs)
  const [newEntityCounter, setNewEntityCounter] = useState(0);

  // Fetch entities from Commenda OS
  const {
    data: entities,
    isLoading,
    error,
  } = useCommendaOSList<Record<string, unknown>>(
    entity,
    dslContext as { companyId: number } | undefined,
    listPath
  );

  // Get selected entity IDs from form data
  const selectedEntityIds = useMemo(() => {
    const value = formData[fieldId];
    if (Array.isArray(value)) return value as string[];
    if (value) return [String(value)];
    return [];
  }, [formData, fieldId]);

  // Helper to get nested value from object
  const getNestedValue = useCallback(
    (obj: Record<string, unknown>, path: string): unknown => {
      return path.split('.').reduce((current: unknown, key: string) => {
        if (current && typeof current === 'object') {
          return (current as Record<string, unknown>)[key];
        }
        return undefined;
      }, obj);
    },
    []
  );

  // Transform entities into select options
  const options = useMemo(() => {
    if (!entities || !Array.isArray(entities)) return [];

    return entities.map((entityItem) => {
      const displayValue = getNestedValue(entityItem, displayField);
      const idValue = getNestedValue(entityItem, valueField);

      return {
        value: String(idValue ?? ''),
        label: String(displayValue ?? `${entity} ${idValue}`),
      };
    });
  }, [entities, displayField, valueField, entity, getNestedValue]);

  // The inline template, checked like a linked form. Each field is read with the entity's own
  // context, as InlineFieldsContainer prefills it, so the authored bindings are dropped.
  const templateDsl = useMemo(
    () =>
      template.length > 0
        ? LinkedFormDslHelper.wrapModule({
            id: `${fieldId}-template`,
            name: '',
            order: 0,
            components: template.map((field) => ({
              ...field,
              fieldId: field.fieldId || field.id,
              dataSource: field.dataSource?.commendaOS
                ? {
                    ...field.dataSource,
                    commendaOS: {
                      ...field.dataSource.commendaOS,
                      contextBindings: [],
                    },
                  }
                : field.dataSource,
            })),
          })
        : null,
    [template, fieldId]
  );
  const completenessTargets = useMemo(
    (): EntryCompletenessTarget[] =>
      selectedEntityIds
        .filter((entityId) => !entityId.startsWith('new-'))
        .map((entityId) => {
          const values: Record<string, unknown> = {};
          template.forEach((field) => {
            const templateFieldId = field.fieldId || field.id;
            const value =
              formData[`${fieldId}-entity-${entityId}-${templateFieldId}`];
            if (value !== undefined) values[templateFieldId] = value;
          });
          const numericId = Number(entityId);
          return {
            entryId: entityId,
            readContext: dslContext?.companyId
              ? // A FormDSLContext with a companyId is a DSLContext.
                ({
                  ...dslContext,
                  [idContextKey || `${entity}Id`]: numericId,
                  ...(entity === 'keyPerson' ? { directorId: numericId } : {}),
                } as DSLContext)
              : undefined,
            values,
          };
        }),
    [
      selectedEntityIds,
      template,
      formData,
      fieldId,
      dslContext,
      idContextKey,
      entity,
    ]
  );
  const { statusByEntryId } = useEntryCompleteness({
    linkedForm: templateDsl
      ? { dsl: templateDsl, isLoading: false, hasFailed: false }
      : null,
    targets: completenessTargets,
  });

  // Check if an entity has missing required fields
  const checkEntityMissingFields = useCallback(
    (entityId: string, isNew: boolean): boolean => {
      if (template.length === 0) return false;

      // For new entities, check if any required field is missing
      if (isNew) {
        return template.some((field) => {
          const entityFieldId = `${fieldId}-entity-${entityId}-${field.fieldId || field.id}`;
          const value = formData[entityFieldId];
          return value === undefined || value === null || value === '';
        });
      }

      return !!statusByEntryId[entityId];
    },
    [template, fieldId, formData, statusByEntryId]
  );

  // Build selected entities with display names and status
  const selectedEntities: SelectedEntity[] = useMemo(() => {
    return selectedEntityIds.map((id) => {
      const isNew = id.startsWith('new-');
      const hasMissingFields = checkEntityMissingFields(id, isNew);

      if (isNew) {
        // For new entities, try to get a display name from the first template field
        let displayName = 'New Entity';
        for (const field of template) {
          const entityFieldId = `${fieldId}-entity-${id}-${field.fieldId || field.id}`;
          const value = formData[entityFieldId];
          if (value && typeof value === 'string') {
            displayName = value;
            break;
          }
        }

        return {
          id,
          displayName,
          isNew: true,
          hasMissingFields,
        };
      }

      // For existing entities, find the display name from options
      const option = options.find((opt) => opt.value === id);
      return {
        id,
        displayName: option?.label || `Entity ${id}`,
        isNew: false,
        hasMissingFields,
        completenessStatus: statusByEntryId[id],
      };
    });
  }, [
    selectedEntityIds,
    options,
    formData,
    fieldId,
    template,
    checkEntityMissingFields,
    statusByEntryId,
  ]);

  // Check if any new entity has missing fields and set/clear error accordingly
  const hasNewEntitiesWithMissingFields = useMemo(() => {
    return selectedEntities.some((e) => e.isNew && e.hasMissingFields);
  }, [selectedEntities]);

  const disabled = propsDisabled || isFieldDisabled(fieldId);
  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);

  // Build effective validation rules including min/max selection
  const effectiveValidationRules = useMemo(() => {
    const rules = { ...validation?.rules };

    // Add minLength rule for minimum selection
    if (minSelection > 0) {
      rules.minLength = {
        enabled: true,
        value: minSelection,
        message: `Please select at least ${minSelection} ${entity}${minSelection > 1 ? 's' : ''}`,
      };
    }

    // Add maxLength rule for maximum selection
    if (maxSelection > 0) {
      rules.maxLength = {
        enabled: true,
        value: maxSelection,
        message: `Please select no more than ${maxSelection} ${entity}${maxSelection > 1 ? 's' : ''}`,
      };
    }

    // If minSelection > 0, also make it required
    if (minSelection > 0) {
      rules.required = {
        enabled: true,
        message: `Please select at least ${minSelection} ${entity}${minSelection > 1 ? 's' : ''}`,
      };
    }

    return rules;
  }, [validation?.rules, minSelection, maxSelection, entity]);

  const missingFieldsError = hasNewEntitiesWithMissingFields
    ? `Please fill in the required fields for new ${entity}s`
    : EntryCompletenessHelper.getBlockingError(
        selectedEntities.flatMap((e) =>
          !e.isNew && e.completenessStatus
            ? [{ name: e.displayName, status: e.completenessStatus }]
            : []
        )
      );

  // Set error when a selected entity has missing fields; this ensures the form's
  // validateAllSteps will catch it. Once they are all filled, re-run the selection's
  // own rules so the error this wrote is replaced rather than left holding the step.
  const hadMissingFieldsErrorRef = useRef(false);
  useEffect(() => {
    if (template.length === 0) return;
    if (missingFieldsError) {
      hadMissingFieldsErrorRef.current = true;
      validateField(fieldId, null, {
        required: { enabled: true, message: missingFieldsError },
      });
    } else if (hadMissingFieldsErrorRef.current) {
      hadMissingFieldsErrorRef.current = false;
      validateField(fieldId, selectedEntityIds, effectiveValidationRules);
    }
  }, [
    missingFieldsError,
    fieldId,
    validateField,
    template.length,
    selectedEntityIds,
    effectiveValidationRules,
  ]);

  // Custom validation for EntitySelectOrCreate (for new entities with missing fields)
  const validateNewEntityFields = useCallback(
    (values: string[]): string | null => {
      // Check if new entities have required fields filled
      if (template.length > 0) {
        for (const entityId of values) {
          const isNew = entityId.startsWith('new-');
          if (isNew) {
            // For new entities, check if any template field is filled
            const hasAnyField = template.some((field) => {
              const entityFieldId = `${fieldId}-entity-${entityId}-${field.fieldId || field.id}`;
              const value = formData[entityFieldId];
              return value !== undefined && value !== null && value !== '';
            });

            if (!hasAnyField) {
              return `Please fill in the required fields for new ${entity}s`;
            }
          }
        }
      }

      return null;
    },
    [entity, template, fieldId, formData]
  );

  // Handle selection changes from MultiSelect
  const handleSelectionChange = useCallback(
    (newValues: string[]) => {
      updateField(fieldId, newValues);

      // Validate with effective rules
      validateField(fieldId, newValues, effectiveValidationRules);

      // Also validate new entity fields
      const newEntityError = validateNewEntityFields(newValues);
      if (newEntityError) {
        // Set the error for the field
        validateField(fieldId, null, {
          required: { enabled: true, message: newEntityError },
        });
      }
    },
    [
      fieldId,
      updateField,
      validateField,
      effectiveValidationRules,
      validateNewEntityFields,
    ]
  );

  // Handle adding a new entity
  const handleAddNew = useCallback(() => {
    const newId = `new-${Date.now()}-${newEntityCounter}`;
    setNewEntityCounter((prev) => prev + 1);

    const newSelection = [...selectedEntityIds, newId];
    updateField(fieldId, newSelection);

    // Auto-expand the new entity for editing
    setExpandedEntityIds((prev) => new Set([...Array.from(prev), newId]));
  }, [selectedEntityIds, fieldId, updateField, newEntityCounter]);

  // Handle removing an entity
  const handleRemoveEntity = useCallback(
    (entityId: string) => {
      const newSelection = selectedEntityIds.filter((id) => id !== entityId);
      updateField(fieldId, newSelection);

      // Remove from expanded set
      setExpandedEntityIds((prev) => {
        const next = new Set(prev);
        next.delete(entityId);
        return next;
      });

      // Clean up form data for this entity's fields
      // Note: This is handled by the form cleanup logic
    },
    [selectedEntityIds, fieldId, updateField]
  );

  // Handle toggling entity expansion
  const handleToggleExpand = useCallback((entityId: string) => {
    setExpandedEntityIds((prev) => {
      const next = new Set(prev);
      if (next.has(entityId)) {
        next.delete(entityId);
      } else {
        next.add(entityId);
      }
      return next;
    });
  }, []);

  // Check if we can add more entities
  const canAddMore =
    maxSelection === 0 || selectedEntityIds.length < maxSelection;

  // Show loading state
  if (isLoading) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(label || description) && (
          <LabelWithGuidanceTooltip
            label={label}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={description}
          />
        )}
        <div className="flex items-center justify-center p-4 border rounded-md bg-bg-secondary">
          <LoadingIndicator size={16} />
          <span className="ml-2 text-sm text-text-tertiary">
            Loading {entity}s...
          </span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(label || description) && (
          <LabelWithGuidanceTooltip
            label={label}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={description}
          />
        )}
        <div className="p-4 border border-border-error rounded-md bg-bg-error-subtle">
          <p className="text-sm text-text-error">
            Failed to load {entity}s. Please try again.
          </p>
        </div>
      </div>
    );
  }

  // Show empty state if no context
  if (!dslContext?.companyId) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(label || description) && (
          <LabelWithGuidanceTooltip
            label={label}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={description}
          />
        )}
        <div className="p-4 border border-border-warning rounded-md bg-bg-warning-subtle">
          <p className="text-sm text-text-warning">
            Company context required to load {entity}s.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4" data-field-id={fieldId}>
      {(label || description) && (
        <LabelWithGuidanceTooltip
          label={label}
          guidance={guidance?.text}
          isRequired={isRequired}
          description={description}
        />
      )}

      {/* MultiSelect Dropdown */}
      <MultiSelect
        id={`${fieldId}-multiselect`}
        options={options}
        onValueChange={handleSelectionChange}
        defaultValue={selectedEntityIds}
        placeholder={selectLabel || placeholder || `Add or select ${entity}s`}
        hideSelectAll
        hideTags
        disabled={disabled}
        error={errors[fieldId]}
        maxSelectable={maxSelection === 0 ? null : maxSelection}
        addItemConfig={
          canAddMore
            ? {
                shouldAllowAdd: true,
                onAddItem: handleAddNew,
                placeholder: addNewLabel || `Add new ${entity}`,
              }
            : undefined
        }
      />

      {/* Selected Entities List */}
      {selectedEntities.length > 0 && (
        <div className="mt-3 space-y-2">
          {selectedEntities.map((selectedEntity) => (
            <div key={selectedEntity.id}>
              <EntityCard
                entity={selectedEntity}
                entityType={entity}
                onRemove={handleRemoveEntity}
                onToggleExpand={handleToggleExpand}
                isExpanded={expandedEntityIds.has(selectedEntity.id)}
                disabled={disabled}
              />

              {/* Inline Fields Container - shown when expanded */}
              {expandedEntityIds.has(selectedEntity.id) &&
                template.length > 0 && (
                  <InlineFieldsContainer
                    parentFieldId={fieldId}
                    entityId={selectedEntity.id}
                    entityType={entity}
                    isNewEntity={selectedEntity.isNew}
                    template={template}
                    idContextKey={idContextKey}
                  />
                )}
            </div>
          ))}
        </div>
      )}

      {/* Validation Error */}
      {errors[fieldId] && (
        <p className="mt-1 text-sm text-text-error">{errors[fieldId]}</p>
      )}

      {/* Selection count hint */}
      {(minSelection > 0 || maxSelection > 0) && (
        <p className="mt-1 text-xs text-text-tertiary">
          {minSelection > 0 && maxSelection > 0 && minSelection === maxSelection
            ? `Select exactly ${minSelection} ${entity}${minSelection > 1 ? 's' : ''}`
            : minSelection > 0 && maxSelection > 0
              ? `Select ${minSelection} to ${maxSelection} ${entity}s`
              : minSelection > 0
                ? `Select at least ${minSelection} ${entity}${minSelection > 1 ? 's' : ''}`
                : maxSelection > 0
                  ? `Select up to ${maxSelection} ${entity}${maxSelection > 1 ? 's' : ''}`
                  : null}
        </p>
      )}
    </div>
  );
};
